import { useRef, useState } from "react";
import { ApiError } from "../../shared/api/client";
import { TouchSheet } from "./TouchSheet";
import { productionGateway } from "./production-gateway";
import { decodeCasePackagingConfirmation, type CasePackagingConfirmation } from "./case-packaging";
import { caseContentSuggestion } from "./case-content-suggestion";
import type { ProductionRun } from "./models";

export function CasePackagingSheet({ run, terminalCode, sessionId, close, onSaved, onContractChanged }: {
  run: ProductionRun; terminalCode: string; sessionId: string; close: () => void;
  onSaved: (value: CasePackagingConfirmation) => void; onContractChanged: () => void;
}) {
  const suggestion = caseContentSuggestion(run.productName);
  const [quantity, setQuantity] = useState(suggestion === null ? "" : String(suggestion));
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attempt = useRef<{ quantity: number; id: string } | null>(null);
  const inFlight = useRef(false);
  const value = /^\d+$/u.test(quantity) ? Number(quantity) : null;
  const valid = value !== null && Number.isSafeInteger(value) && value >= 1 && value <= 1_000_000;
  const save = async () => {
    if (!valid || value === null || !confirmed || inFlight.current) return;
    inFlight.current = true;
    setSaving(true); setError(null);
    if (attempt.current?.quantity !== value) attempt.current = { quantity: value, id: crypto.randomUUID() };
    try {
      const response = decodeCasePackagingConfirmation(await productionGateway.confirmCasePackaging(run.id, terminalCode, {
        boxesPerCase: value, expectedVersion: run.version, expectedSessionId: sessionId, idempotencyKey: attempt.current.id,
      }));
      if (!response) throw new Error("Koli içeriğinin kaydedildiği doğrulanamadı.");
      onSaved(response);
      close();
    } catch (reason: unknown) {
      if (reason instanceof ApiError && reason.code === "PRODUCTION_PACKAGING_MIKRO_AVAILABLE") onContractChanged();
      setError(reason instanceof Error ? reason.message : "Kayıt doğrulanamadı. Aynı değerle yeniden deneyebilirsiniz.");
    }
    finally { inFlight.current = false; setSaving(false); }
  };
  return <TouchSheet title="Bu işin koli içeriği" close={close} closeDisabled={saving} variant="quantity"
    actions={<><button type="button" disabled={saving} onClick={close}>Vazgeç</button><button type="button" disabled={!valid || !confirmed || saving} onClick={() => void save()}>{saving ? "Kaydediliyor…" : "Doğrula ve kaydet"}</button></>}>
    <form className="touch-form operator-packaging-form" onSubmit={(event) => { event.preventDefault(); void save(); }}>
      <p>{run.productName}</p>
      <label htmlFor="case-pack-count">Bir kolide sayacın saydığı kaç ürün var?</label>
      <input id="case-pack-count" inputMode="numeric" pattern="[0-9]*" autoComplete="off" value={quantity} disabled={saving}
        aria-describedby="case-pack-help" onChange={(event) => { setQuantity(event.target.value); setConfirmed(false); }} />
      <p id="case-pack-help">Tek ürün; sayacın bir kez saydığı kutu, silindir veya ilgili fiziksel üründür. Gramajı ya da ürünün içindeki parçaları girmeyin.</p>
      {suggestion !== null ? <p className="operator-packaging-hint">Ürün adından öneri: <strong>{suggestion}</strong>. Paket üzerindeki bilgiyle doğrulayın.</p> : null}
      <label className="operator-packaging-check"><input type="checkbox" checked={confirmed} disabled={saving} onChange={(event) => setConfirmed(event.target.checked)} /><span>Bir kolinin içeriğini ve sayacın hangi ürünü saydığını kontrol ettim.</span></label>
      <p>Yalnız bu iş için kaydedilir. Mikro kartı ve geçmiş üretim kayıtları değiştirilmez.</p>
      {run.status === "ACTIVE" ? <p>İşin sayımı başlamışsa mevcut hesap katsayısı değiştirilmez; kayıt yalnız koli içeriği bilgisi olarak saklanabilir.</p> : null}
      {error ? <p role="alert">{error}</p> : null}
    </form>
  </TouchSheet>;
}
